import { useCallback, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { InjectionTokens } from '@/controller/tokens'

import { NotamFeature } from '@/domain/models/Notam'

import { INotamRepository } from '@/data/NotamRepository/INotamRepository'

import { NotamItem } from '../NotamItem/NotamItem'
import { HighlightOnMapButton, MessageContainer, NotamItemContainer, NotamListContainer } from './InfoPanel.styles'

import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useI18n } from '@/presentation/hooks/useI18n'
import { MapImperativeInterface } from '@/presentation/modules/MapScreen/interfaces/MapImperativeInterface'
import {
  HighlightedNotamsState,
  HighlightedNotamsStateMutator
} from '@/presentation/modules/MapScreen/states/HighlightedNotamsState'

interface AirspaceNotamProps {
  airspaceCode: string
}

function AirspaceNotamList(props: AirspaceNotamProps) {
  const { airspaceCode } = props
  const notamRepository = useInjection<INotamRepository>(InjectionTokens.NotamRepository)
  const theme = useTheme()
  const { t } = useI18n()

  const highlightedNotamsState = useBehaviorSubject(HighlightedNotamsState)

  const getLocalityFromCode = (code: string): string => {
    const parts = code.split('_')
    return parts[0]
  }

  const notams = useMemo(() => {
    const code = getLocalityFromCode(airspaceCode)
    return notamRepository.getByLocality(code)
  }, [airspaceCode, notamRepository])

  const handleToggleHighlightOnMap = useCallback((notam: NotamFeature) => {
    const isHighlighted = HighlightedNotamsStateMutator.isHighlighted(notam.id)

    if (!isHighlighted) {
      HighlightedNotamsStateMutator.clear()
      HighlightedNotamsStateMutator.toggleNotam(notam)

      if (notam.bbox) {
        MapImperativeInterface.flyToBounds(
          [notam.bbox[0], notam.bbox[1], notam.bbox[2], notam.bbox[3]],
          [250, 450, 250, 250]
        )
      }
    } else {
      HighlightedNotamsStateMutator.toggleNotam(notam)
    }
  }, [])

  return (
    <NotamListContainer>
      {notams && notams.length > 0 ? (
        notams.map((item) => {
          const isActive = highlightedNotamsState.highlightedIds.has(item.id)
          return (
            <NotamItemContainer
              key={String(item.id)}
              $backgroundColor={isActive ? theme.colors.secondary : theme.colors.secondaryD1}
            >
              <NotamItem
                notam={item.properties}
                actions={
                  <HighlightOnMapButton
                    label={isActive ? t('NOTAM_REMOVE_FROM_MAP') : t('NOTAM_VIEW_ON_MAP')}
                    onPress={() => handleToggleHighlightOnMap(item)}
                    color="secondary"
                    variant="filled"
                    type="button"
                    size="sm"
                    fullWidth
                    rightIcon={isActive ? 'Close2' : null}
                    buttonColor={isActive ? theme.colors.secondaryL1 : theme.colors.secondary}
                  />
                }
              />
            </NotamItemContainer>
          )
        })
      ) : (
        <MessageContainer>{t('NOTAM_NO_NOTAMS_FOUND_AIRSPACE')}</MessageContainer>
      )}
    </NotamListContainer>
  )
}

export { AirspaceNotamList }
